/* eslint-disable no-param-reassign */
import { Appointments } from '@prisma/client';
import { format, isWithinInterval as fnsIsWithinInterval } from 'date-fns';
import { capitalize } from '@mui/material';
import {
  DateType,
  GetAppointmentDataParams,
  GetOrderDataParams,
  Payment,
  ReportAppointment,
  ReportOrder,
} from '../../types/reports';
import { Appointment } from '../../interfaces';
import { getAppointmentTipAmount } from '../appointments';
import {
  checkPaymentType,
  getPaymentMethodText,
  getPaymentWithMetadata,
  isCardPaymentMethod,
  isPaymentComplete,
  sortPaymentsByPaymentMethod,
} from '../payments';
import { dedupeList, formatDollarAmount } from '../../utils/global/helper';
import { getFullName } from '../global';
import { getOrderItemName } from '../orders';
import {
  getAppointmentPaymentPaidAmountWithLineItems,
  getOrderPaymentPaidAmountWithLineItems,
} from './line-items';

const isWithinInterval = (...args: Parameters<typeof fnsIsWithinInterval>) => {
  try {
    return fnsIsWithinInterval(...args);
  } catch {
    return false;
  }
};

const checkIfAmountInMetadata = (payment: Payment) =>
  !!payment?.metadata &&
  ('service_paid_amount' in payment.metadata ||
    'retail_paid_amount' in payment.metadata);

const checkIfSalesTaxInMetadata = (payment: Payment, giftCard?: boolean) => {
  const keys = ['order_sales_tax', 'appointment_sales_tax'];
  if (giftCard)
    keys.push('retail_gift_card_sales_tax', 'service_gift_card_sales_tax');
  return !!payment.metadata && keys.some(key => key in payment.metadata);
};

const checkIfGiftCardAmountInMetadata = (payment: Payment) =>
  !!payment?.metadata &&
  ('retail_gift_card_amount' in payment.metadata ||
    'service_gift_card_amount' in payment.metadata);

const checkIfTipAmountInMetadata = (payment: Payment) =>
  !!payment?.metadata && 'tip_paid_amount' in payment.metadata;

const filterPayments = <TPayment extends Payment>(
  payments: TPayment[],
  includeNonSuccess = true,
) =>
  includeNonSuccess ? payments : payments.filter(p => p.status === 'SUCCESS');

const hasImportPayment = (payments: Payment[]) =>
  payments.some(p => p.type === 'IMPORT');

const getAppointmentFullPaymentMethod = (payments: Payment[]) => {
  const completePayments = payments.filter(isPaymentComplete);
  const hasImport = hasImportPayment(completePayments);
  const fullPayments = hasImport
    ? completePayments
    : completePayments.filter(p => checkPaymentType(p, 'FULL'));
  const paymentMethod = dedupeList(
    sortPaymentsByPaymentMethod(fullPayments).map(p =>
      getPaymentMethodText(p.method),
    ),
  ).join('/');
  return paymentMethod;
};

const hasLineItems = (
  payments:
    | (ReportAppointment | ReportOrder)['payments']
    | (ReportAppointment | ReportOrder)['payments'][number],
) => {
  if (Array.isArray(payments)) {
    return payments.some(p => p.lineItems.length);
  }
  return !!payments.lineItems.length;
};

export const isAppointmentCompleteWithinRange = (
  appointment: Appointments,
  startDate: Date | null,
  endDate: Date | null,
) =>
  !!appointment.checkoutCompletedOn &&
  isWithinInterval(new Date(appointment.checkoutCompletedOn), {
    start: startDate ?? new Date(),
    end: endDate ?? new Date(),
  });

export const isDepositPaidWithinRange = (
  appointment: Appointments | Appointment,
  startDate: Date | null,
  endDate: Date | null,
) =>
  !!(appointment.depositPaidOn || appointment.confirmedOn) &&
  isWithinInterval(
    new Date((appointment.depositPaidOn || appointment.confirmedOn)!),
    {
      start: startDate ?? new Date(),
      end: endDate ?? new Date(),
    },
  );

export const isAppointmentConfirmedWithinRange = (
  appointment: Appointments,
  startDate: Date | null,
  endDate: Date | null,
) =>
  !!appointment.confirmedOn &&
  isWithinInterval(new Date(appointment.confirmedOn), {
    start: startDate ?? new Date(),
    end: endDate ?? new Date(),
  });

export const isDepositEverPaid = (appointment: Appointments | Appointment) =>
  !!appointment.depositPaidOn || !!appointment.confirmedOn;

export const getAppointmentDepositPaidWithoutRefund = ({
  appointment,
}: {
  appointment: Appointments;
}) => {
  if (!isDepositEverPaid(appointment)) return 0;
  return appointment.isDepositRefunded ? 0 : appointment.depositPrice ?? 0;
};

export const getAppointmentReschedulingFeesPaidWithoutRefund = ({
  appointment,
}: {
  appointment: Appointments | Appointment | null;
}) => {
  if (!appointment?.reschedulingFeesPaid) return 0;
  return Math.max(
    0,
    (appointment.reschedulingFeesPaid ?? 0) -
      (appointment.reschedulingFeesRefundAmount ?? 0),
  );
};

export const getAppointmentArtistName = ({ employee }: ReportAppointment) => {
  if (employee.displayName) return employee.displayName;
  const name = `${employee.firstName || ''} ${employee.lastName || ''}`;
  return name.trim() || 'Unknown';
};

export const getAppointmentCustomerName = ({ customer }: ReportAppointment) => {
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`;
  return name.trim() || 'Unknown';
};

export const getAppointmentDepositPaid = ({
  appointment,
  dateType,
  startDate,
  endDate,
}: {
  appointment: Appointments | Appointment;
  dateType: DateType;
  startDate?: Date | null;
  endDate?: Date | null;
}) => {
  const isDepositPaid = isDepositEverPaid(appointment);

  if (dateType === 'appointment')
    return isDepositPaid ? appointment.depositPrice ?? 0 : 0;

  const isPaidWithinRange = isDepositPaidWithinRange(
    appointment,
    startDate ?? null,
    endDate ?? null,
  );

  return isPaidWithinRange ? appointment.depositPrice ?? 0 : 0;
};

const getTipPaidAmount = (payment: Payment) => {
  const hasTipPaidAmountInMetadata = checkIfTipAmountInMetadata(payment);
  const tipPaidAmount = Number(
    hasTipPaidAmountInMetadata
      ? +(payment.metadata?.tip_paid_amount ?? 0)
      : +(payment.metadata?.tip_amt ?? 0),
  );
  return tipPaidAmount;
};

export const getAppointmentCreditSales = ({
  appointment,
  includeTips,
  includeTaxes,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  includeTips: boolean;
  includeTaxes: boolean;
  includeNonSuccess?: boolean;
}) => {
  const creditPayments = filterPayments(
    appointment?.payments?.filter(payment =>
      isCardPaymentMethod(payment.method),
    ),
    includeNonSuccess,
  );

  const hasImport = hasImportPayment(creditPayments);
  let chargeTotal = creditPayments?.reduce((currentChargeTotal, payment) => {
    if (hasLineItems(payment)) {
      return (
        currentChargeTotal +
        getAppointmentPaymentPaidAmountWithLineItems({
          payment,
          includeTaxes,
          includeTips,
        })
      );
    }

    const { amount: paymentAmount, metadata } = payment;
    const hasAmountInMetadata = checkIfAmountInMetadata(payment);
    const hasSalesTaxInMetadata = checkIfSalesTaxInMetadata(payment);
    const amount = hasAmountInMetadata
      ? +(metadata?.service_paid_amount || 0)
      : +(paymentAmount || 0);
    const feeAmount = hasAmountInMetadata ? 0 : +(metadata?.service_fee ?? 0);
    let thisChargeTotal = (amount - feeAmount) / 100;
    const taxAmount = Math.min(
      thisChargeTotal,
      (hasSalesTaxInMetadata
        ? Math.min(
            Number(
              (metadata.sales_tax_paid_amount ??
                metadata.appointment_sales_tax) ||
                0,
            ),
            Number(metadata.appointment_sales_tax || 0),
          )
        : Number(metadata?.sales_tax || 0)) / 100,
    );
    if (!includeTaxes && taxAmount > 0) {
      thisChargeTotal -= taxAmount;
    }

    const tipAmount = hasImport ? 0 : getTipPaidAmount(payment);
    if (!includeTips && tipAmount > 0) {
      thisChargeTotal -= tipAmount / 100;
    }

    const total = currentChargeTotal + thisChargeTotal;
    return total;
  }, 0);

  const tipFromCreditPayments = hasImport
    ? getAppointmentTipAmount(creditPayments)
    : 0;
  if (
    !includeTips &&
    chargeTotal &&
    tipFromCreditPayments &&
    !appointment.isTotalRefunded
  ) {
    chargeTotal -= tipFromCreditPayments;
  }

  return chargeTotal;
};

export const getAppointmentCashSalesImports = ({
  appointment,
  startDate,
  endDate,
  includeTips,
  includeTaxes,
  dateType,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  startDate: Date | null;
  endDate: Date | null;
  includeTips: boolean;
  includeTaxes: boolean;
  dateType: DateType;
  includeNonSuccess?: boolean;
}) => {
  if (
    appointment.paymentMethod !== 'CASH' &&
    appointment.depositPaymentMethod !== 'CASH'
  )
    return 0;

  const cashPayments = filterPayments(
    appointment?.payments?.filter(payment => payment.method === 'CASH'),
    includeNonSuccess,
  );
  const cashDepositSalesTax = includeTaxes
    ? (getPaymentWithMetadata(cashPayments, 'DEPOSIT')?.metadata?.sales_tax ??
        0) / 100
    : 0;
  const cashServiceSalesTax = includeTaxes
    ? (getPaymentWithMetadata(cashPayments, 'FULL')?.metadata?.sales_tax ?? 0) /
      100
    : 0;

  // only count tip if it was paid in cash
  const amountTippedCash = getAppointmentTipAmount(cashPayments);

  if (dateType === 'appointment') {
    const isDepositPaid = isDepositEverPaid(appointment);
    const depositToInclude =
      isDepositPaid && appointment.depositPaymentMethod === 'CASH'
        ? appointment.depositPrice ?? 0 + cashDepositSalesTax
        : 0;
    const servicePriceToInclude =
      appointment.paymentMethod === 'CASH'
        ? (appointment.price ?? 0) +
          cashServiceSalesTax -
          // if paid in another transaction, subtract deposit
          (isDepositPaid ? appointment.depositPrice ?? 0 : 0)
        : 0;
    const tipAmount = includeTips ? amountTippedCash ?? 0 : 0;

    return depositToInclude + servicePriceToInclude + tipAmount;
  }

  const isCheckoutComplete = isAppointmentCompleteWithinRange(
    appointment,
    startDate,
    endDate,
  );
  const isDepositPaid = isDepositPaidWithinRange(
    appointment,
    startDate,
    endDate,
  );

  const depositToInclude =
    appointment.depositPaymentMethod === 'CASH' && isDepositPaid
      ? appointment.depositPrice ?? 0 + cashDepositSalesTax
      : 0;
  const servicePriceToInclude =
    appointment.paymentMethod === 'CASH' && isCheckoutComplete
      ? (appointment.price ?? 0) +
        cashServiceSalesTax -
        (isDepositEverPaid(appointment) ? appointment.depositPrice ?? 0 : 0)
      : 0;
  const tipAmount =
    includeTips && isCheckoutComplete ? amountTippedCash ?? 0 : 0;
  const total = depositToInclude + servicePriceToInclude + tipAmount;

  return total;
};

export const getAppointmentCashSales = ({
  appointment,
  startDate,
  endDate,
  includeTips,
  includeTaxes,
  dateType,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  startDate: Date | null;
  endDate: Date | null;
  includeTips: boolean;
  includeTaxes: boolean;
  dateType: DateType;
  includeNonSuccess?: boolean;
}) => {
  const hasImports = hasImportPayment(appointment.payments);
  if (hasImports) {
    return getAppointmentCashSalesImports({
      appointment,
      startDate,
      endDate,
      includeTips,
      includeTaxes,
      dateType,
      includeNonSuccess,
    });
  }

  const cashPayments = filterPayments(
    appointment?.payments?.filter(payment => payment.method === 'CASH'),
    includeNonSuccess,
  );

  const hasImport = hasImportPayment(cashPayments);
  let chargeTotal = cashPayments?.reduce((currentChargeTotal, payment) => {
    if (hasLineItems(payment)) {
      return (
        currentChargeTotal +
        getAppointmentPaymentPaidAmountWithLineItems({
          payment,
          includeTaxes,
          includeTips,
        })
      );
    }

    const hasAmountInMetadata = checkIfAmountInMetadata(payment);
    const hasSalesTaxInMetadata = checkIfSalesTaxInMetadata(payment);
    const { amount: paymentAmount, metadata } = payment;
    const amount = hasAmountInMetadata
      ? +(metadata?.service_paid_amount || 0)
      : +(paymentAmount || 0);
    const feeAmount = hasAmountInMetadata ? 0 : +(metadata?.service_fee ?? 0);
    let thisChargeTotal = (amount - feeAmount) / 100;
    const taxAmount = Math.min(
      thisChargeTotal,
      (hasSalesTaxInMetadata
        ? Math.min(
            Number(
              (metadata.sales_tax_paid_amount ??
                metadata.appointment_sales_tax) ||
                0,
            ),
            Number(metadata.appointment_sales_tax || 0),
          )
        : Number(metadata?.sales_tax || 0)) / 100,
    );
    if (!includeTaxes && taxAmount > 0) {
      thisChargeTotal -= taxAmount;
    }
    const tipAmount = hasImport ? 0 : getTipPaidAmount(payment);
    if (!includeTips && tipAmount > 0) {
      thisChargeTotal -= tipAmount / 100;
    }

    const total = currentChargeTotal + thisChargeTotal;
    return total;
  }, 0);

  const tipFromCashPayments = hasImport
    ? getAppointmentTipAmount(cashPayments)
    : 0;
  if (
    !includeTips &&
    chargeTotal &&
    tipFromCashPayments &&
    !appointment.isTotalRefunded
  ) {
    chargeTotal -= tipFromCashPayments;
  }

  return chargeTotal;
};

const getSelectedProportion = (
  order: ReportOrder,
  selectedProducts: string[],
) => {
  const areAllItemsTaxed =
    order.orderItems.every(i => !i.taxRate) &&
    order.payments.some(
      p => p.type === 'ORDER' && +(p.metadata?.sales_tax || 0) > 0,
    );

  const totalSalesTax = !areAllItemsTaxed
    ? order.orderItems.reduce(
        (sum, item) =>
          sum +
          (Math.round((item.customItemPrice || 0) * 100 * item.taxRate) / 100) *
            item.quantity,
        0,
      )
    : order.payments.reduce(
        (sum, p) =>
          sum +
          Number(
            ('order_sales_tax' in (p.metadata || {})
              ? p.metadata.order_sales_tax
              : p.metadata?.sales_tax) || 0,
          ) /
            100,
        0,
      );

  const totalAmount = order.orderItems.reduce(
    (sum, item) => sum + (item.customItemPrice || 0) * item.quantity,
    0,
  );

  const taxRate = areAllItemsTaxed ? totalSalesTax / totalAmount : null;
  const total = totalAmount + Math.round(totalSalesTax * 100) / 100;

  const selectedItems = order.orderItems.filter(item =>
    selectedProducts.includes(getOrderItemName(item)),
  );

  const selectedTaxAmount = selectedItems.reduce(
    (sum, item) =>
      sum +
      Math.round(
        Math.round(
          (item.customItemPrice || 0) * 100 * (taxRate || item.taxRate),
        ) * item.quantity,
      ) /
        100,
    0,
  );

  const selectedTotalProportion =
    total > 0
      ? selectedItems.reduce((sum, item) => {
          const price = item.customItemPrice || 0;
          const tax = item.taxRate || taxRate || 0;
          const itemTotal =
            Math.round(item.quantity * (price + price * tax) * 100) / 100;
          return sum + itemTotal;
        }, 0) / total
      : 0;

  return {
    selectedProportion: selectedTotalProportion,
    totalAmount: total,
    selectedTaxAmount,
  };
};

export const getAppointmentGiftCardSales = ({
  appointment,
  includeTaxes,
  includeTips,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  includeTaxes: boolean;
  includeTips: boolean;
  includeNonSuccess?: boolean;
}) => {
  const giftCardPayments = filterPayments(
    appointment?.payments?.filter(payment => payment.method === 'GIFT_CARD'),
    includeNonSuccess,
  );

  const hasGiftCardAmountInMetadata = giftCardPayments?.some(
    checkIfGiftCardAmountInMetadata,
  );
  const hasSalesTaxInMetadata = giftCardPayments?.some(p =>
    checkIfSalesTaxInMetadata(p, true),
  );

  const giftCardTotal = giftCardPayments?.reduce((currentTotal, payment) => {
    const { amount: paymentAmount, metadata: md } = payment;
    if (hasLineItems(payment)) {
      return (
        currentTotal +
        getAppointmentPaymentPaidAmountWithLineItems({
          payment,
          includeTaxes,
          includeTips,
        })
      );
    }

    const metadata = md || {};
    const amount = hasGiftCardAmountInMetadata
      ? +(metadata?.service_gift_card_amount || 0)
      : +(paymentAmount || 0);

    let thisChargeTotal = amount / 100;
    const mdTaxAmount =
      (hasSalesTaxInMetadata
        ? Number(
            metadata?.service_gift_card_sales_tax ||
              metadata?.appointment_sales_tax ||
              0,
          )
        : +(metadata?.sales_tax ?? 0)) / 100;
    const taxAmount = Math.min(thisChargeTotal, mdTaxAmount);

    if (!includeTaxes && taxAmount > 0) {
      thisChargeTotal -= taxAmount;
    }

    const tipAmount =
      Number(
        ('service_gift_card_tip' in metadata
          ? metadata?.service_gift_card_tip
          : metadata?.tip_amt) || 0,
      ) / 100;

    if (!includeTips && tipAmount > 0) {
      thisChargeTotal -= tipAmount;
    }

    const total = currentTotal + thisChargeTotal;
    return total;
  }, 0);
  return giftCardTotal || 0;
};

export const getOrderPaymentPaidAmount = ({
  payment,
  order,
  selectedProducts,
  includeTaxes,
  includeTips,
}: {
  payment: ReportOrder['payments'][number];
  order: ReportOrder;
  selectedProducts: string[];
  includeTaxes: boolean;
  includeTips: boolean;
}) => {
  if (hasLineItems(payment)) {
    return getOrderPaymentPaidAmountWithLineItems({
      payment,
      order,
      selectedProducts,
      includeTaxes,
      includeTips,
    });
  }

  const { amount: paymentAmount, metadata, appointmentId } = payment;
  const { selectedProportion, selectedTaxAmount } = getSelectedProportion(
    order,
    selectedProducts,
  );

  const hasAmountInMetadata = checkIfAmountInMetadata(payment);
  const hasSalesTaxInMetadata = checkIfSalesTaxInMetadata(payment);

  const amount = hasAmountInMetadata
    ? +(metadata?.retail_paid_amount || 0)
    : +(paymentAmount || 0);

  const feeAmount = hasAmountInMetadata ? 0 : +(metadata?.service_fee ?? 0);
  const amountLessFees = amount - feeAmount;
  const tipAmount = appointmentId ? 0 : getTipPaidAmount(payment);
  const amountLessTip = amountLessFees - tipAmount;

  const proportionAmount = Math.round(amountLessTip * selectedProportion) / 100;
  let thisChargeTotal = proportionAmount;

  if (includeTips && tipAmount > 0) {
    thisChargeTotal += tipAmount / 100;
  }

  const mdTaxAmount =
    (hasSalesTaxInMetadata
      ? Math.min(
          Number(
            (metadata.sales_tax_paid_amount ?? metadata.order_sales_tax) || 0,
          ),
          Number(metadata.order_sales_tax || 0),
        )
      : Number(metadata?.sales_tax || 0)) / 100;
  const taxAmount = Math.min(thisChargeTotal, mdTaxAmount, selectedTaxAmount);

  const selectedTaxProportion = Math.min(1, selectedTaxAmount / taxAmount);

  const taxForSelectedProducts = taxAmount * selectedTaxProportion;

  if (!includeTaxes && taxForSelectedProducts) {
    thisChargeTotal -= taxForSelectedProducts;
  }

  return thisChargeTotal;
};

export const getOrderGiftCardSales = ({
  order,
  includeTaxes,
  includeTips,
  includeNonSuccess = true,
  selectedProducts,
}: {
  order: ReportOrder;
  includeTaxes: boolean;
  includeTips: boolean;
  includeNonSuccess?: boolean;
  selectedProducts: string[];
}) => {
  const giftCardPayments = filterPayments(
    order?.payments?.filter(payment => payment.method === 'GIFT_CARD'),
    includeNonSuccess,
  );

  const { selectedProportion, selectedTaxAmount } = getSelectedProportion(
    order,
    selectedProducts,
  );

  const hasGiftCardAmountInMetadata = giftCardPayments?.some(
    checkIfGiftCardAmountInMetadata,
  );
  const hasSalesTaxInMetadata = giftCardPayments?.some(p =>
    checkIfSalesTaxInMetadata(p, true),
  );

  const giftCardTotal = giftCardPayments?.reduce((currentTotal, payment) => {
    const { amount: paymentAmount, metadata } = payment;

    if (hasLineItems(payment)) {
      return getOrderPaymentPaidAmountWithLineItems({
        payment,
        order,
        selectedProducts,
        includeTaxes,
        includeTips,
      });
    }

    const amount = hasGiftCardAmountInMetadata
      ? +(metadata?.retail_gift_card_amount || 0)
      : +(paymentAmount || 0);
    let thisChargeTotal = Math.round(amount * selectedProportion) / 100;
    const mdTaxAmount = hasSalesTaxInMetadata
      ? Number(
          (metadata?.retail_gift_card_sales_tax ?? metadata?.order_sales_tax) ||
            0,
        ) / 100
      : +(metadata?.sales_tax ?? 0) / 100;
    const taxAmount = Math.min(thisChargeTotal, mdTaxAmount, selectedTaxAmount);
    const selectedTaxProportion =
      taxAmount > 0 ? Math.min(1, selectedTaxAmount / taxAmount) : 0;
    const taxForSelectedProducts = taxAmount * selectedTaxProportion;
    if (!includeTaxes && taxForSelectedProducts > 0) {
      thisChargeTotal -= taxForSelectedProducts;
    }
    const tipAmount =
      Number(
        ('retail_gift_card_tip' in metadata
          ? metadata?.retail_gift_card_tip
          : metadata?.tip_amt) || 0,
      ) / 100;
    if (!includeTips && tipAmount > 0) {
      thisChargeTotal -= tipAmount;
    }
    const total = currentTotal + thisChargeTotal;
    return total;
  }, 0);
  return giftCardTotal || 0;
};

export const getAppointmentTotalSales = ({
  appointment,
  includeTaxes,
  includeTips,
  includeNonSuccess = true,
  startDate,
  endDate,
  dateType,
}: GetAppointmentDataParams) =>
  getAppointmentCashSales({
    appointment,
    startDate,
    endDate,
    includeTips: includeTips ?? false,
    includeTaxes: includeTaxes ?? false,
    includeNonSuccess,
    dateType,
  }) +
  getAppointmentCreditSales({
    appointment,
    includeTips: includeTips ?? false,
    includeTaxes: includeTaxes ?? false,
    includeNonSuccess,
  }) +
  getAppointmentGiftCardSales({
    appointment,
    includeTaxes: includeTaxes ?? false,
    includeTips: includeTips ?? false,
    includeNonSuccess,
  });

export const getOrderCreditSales = ({
  order,
  includeTips,
  includeTaxes,
  includeNonSuccess = true,
  selectedProducts,
}: {
  order: ReportOrder;
  includeTips: boolean;
  includeTaxes: boolean;
  includeNonSuccess?: boolean;
  selectedProducts: string[];
}) => {
  const creditPayments = filterPayments(
    order?.payments?.filter(payment => isCardPaymentMethod(payment.method)),
    includeNonSuccess,
  );

  const chargeTotal = creditPayments?.reduce((currentChargeTotal, payment) => {
    const thisChargeTotal = getOrderPaymentPaidAmount({
      payment,
      order,
      selectedProducts,
      includeTaxes,
      includeTips,
    });
    return currentChargeTotal + thisChargeTotal;
  }, 0);

  return chargeTotal;
};

export const getOrderCashSales = ({
  order,
  includeTaxes,
  includeTips,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  includeTaxes: boolean;
  includeTips: boolean;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) => {
  const cashPayments = filterPayments(
    order.payments.filter(payment => payment.method === 'CASH'),
    includeNonSuccess,
  );
  if (!cashPayments.length) return 0;

  const total = cashPayments.reduce((currentTotal, payment) => {
    const thisChargeTotal = getOrderPaymentPaidAmount({
      payment,
      order,
      selectedProducts,
      includeTaxes,
      includeTips,
    });
    return currentTotal + thisChargeTotal;
  }, 0);
  return total;
};

export const getOrderTotalSales = ({
  order,
  includeTips,
  includeTaxes,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  includeTips: boolean;
  includeTaxes: boolean;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) => {
  const cashSales = getOrderCashSales({
    order,
    includeTaxes,
    includeTips,
    selectedProducts,
    includeNonSuccess,
  });
  const creditSales = getOrderCreditSales({
    order,
    includeTips,
    includeTaxes,
    selectedProducts,
    includeNonSuccess,
  });
  const giftCardSales = getOrderGiftCardSales({
    order,
    includeTaxes,
    includeTips,
    selectedProducts,
    includeNonSuccess,
  });

  return cashSales + creditSales + giftCardSales;
};

export const getObjectRefundedAmount = ({
  reportObject,
}: {
  reportObject: ReportAppointment | ReportOrder;
}) => {
  const refundedAmount = reportObject?.payments?.reduce(
    (currentTotal, { refunds }) =>
      currentTotal +
      refunds.reduce(
        (currentRefundTotal, { amount }) => currentRefundTotal + amount / 100,
        0,
      ),
    0,
  );
  return refundedAmount || 0;
};

export const getObjectVoidedAmounts = ({
  reportObject,
}: {
  reportObject: ReportAppointment | ReportOrder;
}) => {
  const voidedPayments = reportObject?.payments?.filter(
    payment => payment.status === 'VOIDED',
  );
  const voidedAmount = voidedPayments?.reduce(
    (currentTotal, { amount, method }) => {
      const dollarAmount = amount / 100;
      if (method === 'CASH') {
        currentTotal.cash += dollarAmount;
      } else if (method === 'GIFT_CARD') {
        currentTotal.giftCard += dollarAmount;
      }
      return currentTotal;
    },
    { cash: 0, giftCard: 0 },
  );
  return voidedAmount;
};

export const getAppointmentTransferAmount = ({
  appointment,
  type,
}: {
  appointment: ReportAppointment;
  type: 'employee' | 'shop';
}) => {
  const chargesTotal = appointment?.payments?.reduce(
    (currentTotal, { transfers }) => {
      if (!transfers) return currentTotal;
      const entity = type.toUpperCase();
      const transferData = transfers.find(
        transfer => transfer.entity === entity,
      );
      if (!transferData?.amount) return currentTotal;
      const chargeTotal = +transferData.amount || 0;
      return currentTotal + chargeTotal / 100;
    },
    0,
  );

  return chargesTotal;
};

export const getAppointmentTotalFees = ({
  totalCredit,
  amountSentToArtist,
  amountSentToShop,
}: {
  totalCredit: number;
  amountSentToArtist: number;
  amountSentToShop: number;
}) => {
  const totalSentOut = amountSentToArtist + amountSentToShop;
  if (totalSentOut <= 0 || totalCredit <= 0) return 0;

  return Math.max(0, totalCredit - totalSentOut);
};

export const getAppointmentCashSalesTax = ({
  appointment,
  startDate,
  endDate,
  dateType,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  startDate: Date | null;
  endDate: Date | null;
  dateType: DateType;
  includeNonSuccess?: boolean;
}) =>
  getAppointmentCashSales({
    appointment,
    startDate,
    endDate,
    dateType,
    includeTaxes: true,
    includeTips: false,
    includeNonSuccess,
  }) -
  getAppointmentCashSales({
    appointment,
    startDate,
    endDate,
    dateType,
    includeTaxes: false,
    includeTips: false,
    includeNonSuccess,
  });

export const getAppointmentCreditSalesTax = ({
  appointment,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  includeNonSuccess?: boolean;
}) =>
  getAppointmentCreditSales({
    appointment,
    includeTaxes: true,
    includeTips: false,
    includeNonSuccess,
  }) -
  getAppointmentCreditSales({
    appointment,
    includeTaxes: false,
    includeTips: false,
    includeNonSuccess,
  });

export const getGiftCardSalesTax = ({
  reportObject,
  type,
  selectedProducts,
  includeNonSuccess = true,
}: {
  reportObject: ReportAppointment | ReportOrder;
  type: 'retail' | 'service';
  selectedProducts?: string[];
  includeNonSuccess?: boolean;
}) => {
  if (type === 'retail') {
    return (
      getOrderGiftCardSales({
        order: reportObject as ReportOrder,
        includeTaxes: true,
        includeTips: true,
        selectedProducts: selectedProducts ?? [],
        includeNonSuccess,
      }) -
      getOrderGiftCardSales({
        order: reportObject as ReportOrder,
        includeTaxes: false,
        includeTips: true,
        selectedProducts: selectedProducts ?? [],
        includeNonSuccess,
      })
    );
  }
  return (
    getAppointmentGiftCardSales({
      appointment: reportObject as ReportAppointment,
      includeTaxes: true,
      includeTips: true,
      includeNonSuccess,
    }) -
    getAppointmentGiftCardSales({
      appointment: reportObject as ReportAppointment,
      includeTaxes: false,
      includeTips: true,
      includeNonSuccess,
    })
  );
};

export const getAppointmentSalesTax = ({
  appointment,
  startDate,
  endDate,
  dateType,
  includeNonSuccess = true,
}: GetAppointmentDataParams) => {
  const payments = filterPayments(appointment.payments, includeNonSuccess);
  const hasAppointmentSalesTax = payments.some(p =>
    checkIfSalesTaxInMetadata(p, true),
  );

  if (hasAppointmentSalesTax) {
    const { cash, credit, giftCard } = payments.reduce(
      (acc, payment) => {
        const { metadata: md, method } = payment;

        const metadata = md || {};

        if (hasLineItems(payment)) {
          const amount = getAppointmentPaymentPaidAmountWithLineItems({
            payment,
            includeTaxes: true,
            includeTips: false,
            lineItemTypes: ['SERVICE_TAX'],
          });
          if (amount > 0) {
            acc.cash += method === 'CASH' ? amount : 0;
            acc.credit +=
              method === 'CREDIT' || method === 'TERMINAL' ? amount : 0;
            acc.giftCard += method === 'GIFT_CARD' ? amount : 0;
          }
        } else if (
          method === 'GIFT_CARD' &&
          'service_gift_card_sales_tax' in metadata
        ) {
          const salesTax =
            Number(metadata.service_gift_card_sales_tax || 0) / 100;
          acc.giftCard += salesTax;
        } else {
          const isDepositOrReschedule = checkPaymentType(payment, [
            'DEPOSIT',
            'RESCHEDULE',
          ]);
          // For older DEPOSITs that do not have sales tax in MD
          const paymentHasSalesTaxInMd = checkIfSalesTaxInMetadata(payment);
          const minValues = [
            Number(metadata.appointment_sales_tax || 0),
            Number(metadata.sales_tax_paid_amount || 0),
          ];
          // deposits to not have service_paid_amount
          if (!isDepositOrReschedule) {
            minValues.push(Number(metadata.service_paid_amount || 0));
          }
          const salesTax = !paymentHasSalesTaxInMd
            ? Number(metadata.sales_tax || 0)
            : 'sales_tax_paid_amount' in metadata
            ? Math.min(...minValues)
            : Number(metadata.appointment_sales_tax);
          if (salesTax) {
            const salesTaxValue = salesTax / 100;
            acc.cash += method === 'CASH' ? salesTaxValue : 0;
            acc.credit +=
              method === 'CREDIT' || method === 'TERMINAL' ? salesTaxValue : 0;
            acc.giftCard += method === 'GIFT_CARD' ? salesTaxValue : 0;
          }
        }
        return acc;
      },
      {
        cash: 0,
        credit: 0,
        giftCard: 0,
      },
    );
    return {
      cash,
      credit,
      giftCard,
      total: cash + credit + giftCard,
    };
  }

  const cash = getAppointmentCashSalesTax({
    appointment,
    startDate,
    endDate,
    dateType,
    includeNonSuccess,
  });

  const credit = getAppointmentCreditSalesTax({
    appointment,
    includeNonSuccess,
  });

  const giftCard = getGiftCardSalesTax({
    reportObject: appointment,
    type: 'service',
    includeNonSuccess,
  });

  const total = cash + credit + giftCard;

  return {
    cash,
    credit,
    giftCard,
    total,
  };
};

const dateFormat = 'MM/dd/yy hh:mm a';

export const getAppointmentDataByAppointment = ({
  dateType,
  endDate,
  includeTips,
  includeTaxes,
  appointment,
  startDate,
}: GetAppointmentDataParams) => {
  const amountSentToArtist = getAppointmentTransferAmount({
    appointment,
    type: 'employee',
  });
  const amountSentToShop = getAppointmentTransferAmount({
    appointment,
    type: 'shop',
  });

  const totalSales = getAppointmentTotalSales({
    appointment,
    dateType,
    includeTips,
    endDate,
    startDate,
    includeTaxes,
  });
  const totalCredit = getAppointmentCreditSales({
    includeTips: includeTips ?? false,
    appointment,
    includeTaxes: includeTaxes ?? false,
  });
  const totalCash = getAppointmentCashSales({
    appointment,
    includeTips: includeTips ?? false,
    startDate,
    endDate,
    dateType,
    includeTaxes: includeTaxes ?? false,
  });
  const totalGiftCard = getAppointmentGiftCardSales({
    appointment,
    includeTaxes: includeTaxes ?? false,
    includeTips: includeTips ?? false,
  });
  const totalFees = getAppointmentTotalFees({
    totalCredit,
    amountSentToArtist,
    amountSentToShop,
  });
  const { total: totalSalesTax } = getAppointmentSalesTax({
    appointment,
    startDate,
    endDate,
    dateType,
  });
  const refundedAmount = Math.min(
    totalCredit,
    getObjectRefundedAmount({ reportObject: appointment }),
  );
  const { cash: voidedCash, giftCard: voidedGiftCard } = getObjectVoidedAmounts(
    {
      reportObject: appointment,
    },
  );
  const voidedAmount =
    Math.min(totalCash, voidedCash) + Math.min(totalGiftCard, voidedGiftCard);

  const depositAmount = getAppointmentDepositPaid({
    appointment,
    dateType,
    endDate,
    startDate,
  });
  const tempServicePricePaid = Math.max(
    0,
    (appointment.price ?? 0) - depositAmount,
  );
  const netDepositAmount = appointment?.isDepositRefunded ? 0 : depositAmount;
  const { isTotalRefunded } = appointment;
  const servicePricePaid = isTotalRefunded ? 0 : tempServicePricePaid;
  const tipAmount = isTotalRefunded
    ? 0
    : getAppointmentTipAmount(appointment.payments);

  const customerName = getAppointmentCustomerName(appointment);
  const employeeName = getAppointmentArtistName(appointment);

  const depositPaymentDate =
    appointment.depositPaidOn || appointment.confirmedOn;

  const paymentMethod = getAppointmentFullPaymentMethod(appointment.payments);

  return {
    appointmentId: appointment.appointmentId,
    totalSales,
    totalCredit,
    totalFees,
    totalCash,
    totalGiftCard,
    amountSentToArtist,
    amountSentToShop,
    checkoutCompletedOn:
      appointment.checkoutCompletedOn && servicePricePaid > 0
        ? format(new Date(appointment.checkoutCompletedOn), dateFormat)
        : 'N/A',
    depositPaymentDate:
      depositPaymentDate && netDepositAmount > 0
        ? format(new Date(depositPaymentDate), dateFormat)
        : 'N/A',
    depositPaymentMethod:
      depositPaymentDate && netDepositAmount > 0
        ? capitalize(
            appointment.depositPaymentMethod?.toLowerCase() ?? 'Credit',
          )
        : 'N/A',
    artist: employeeName,
    customer: customerName,
    date: appointment.startDateTime
      ? format(new Date(appointment.startDateTime), dateFormat)
      : 'N/A',
    deposit: netDepositAmount,
    paymentMethod: servicePricePaid > 0 ? paymentMethod || 'N/A' : 'N/A',
    servicePricePaid,
    tipAmount,
    salesTax: totalSalesTax,
    refundedAmount,
    voidedAmount,
  };
};

export const getAppointmentDataByPayment = ({
  appointment,
  dateType,
  startDate,
  endDate,
  includeTips,
  includeTaxes,
}: GetAppointmentDataParams) => {
  const amountSentToArtist = getAppointmentTransferAmount({
    appointment,
    type: 'employee',
  });
  const amountSentToShop = getAppointmentTransferAmount({
    appointment,
    type: 'shop',
  });

  const isCheckoutComplete = isAppointmentCompleteWithinRange(
    appointment,
    startDate,
    endDate,
  );
  const isDepositPaymentInRange = isDepositPaidWithinRange(
    appointment,
    startDate,
    endDate,
  );

  const { isTotalRefunded, isDepositRefunded } = appointment;

  const depositAmount =
    isDepositPaymentInRange && !isDepositRefunded
      ? appointment.depositPrice ?? 0
      : 0;

  const isDepositPaid = isDepositEverPaid(appointment);
  const subDepositAmt = isDepositPaid ? appointment.depositPrice : 0;
  // will be negative if isConfirmed outside of range and isCheckoutComplete is false
  const tempServicePricePaid = Math.max(
    0,
    (isCheckoutComplete ? appointment.price ?? 0 : 0) - (subDepositAmt ?? 0),
  );
  const tempTipAmount = isCheckoutComplete ? appointment.tipAmt ?? 0 : 0;

  const servicePricePaid = isTotalRefunded ? 0 : tempServicePricePaid;
  const tipAmount = isTotalRefunded ? 0 : tempTipAmount;

  const totalSales = getAppointmentTotalSales({
    appointment,
    includeTips,
    dateType,
    endDate,
    startDate,
    includeTaxes,
  });
  const totalCredit = getAppointmentCreditSales({
    includeTips: includeTips ?? false,
    appointment,
    includeTaxes: includeTaxes ?? false,
  });
  const totalCash = getAppointmentCashSales({
    appointment,
    includeTips: includeTips ?? false,
    startDate,
    endDate,
    dateType,
    includeTaxes: includeTaxes ?? false,
  });
  const totalGiftCard = getAppointmentGiftCardSales({
    appointment,
    includeTaxes: includeTaxes ?? false,
    includeTips: includeTips ?? false,
  });
  const totalFees = getAppointmentTotalFees({
    totalCredit,
    amountSentToArtist,
    amountSentToShop,
  });
  const refundedAmount = Math.min(
    totalCredit,
    getObjectRefundedAmount({ reportObject: appointment }),
  );
  const { cash: voidedCash, giftCard: voidedGiftCard } = getObjectVoidedAmounts(
    {
      reportObject: appointment,
    },
  );
  const voidedAmount =
    Math.min(totalCash, voidedCash) + Math.min(totalGiftCard, voidedGiftCard);

  const hasOutOfRangeDeposit = !isDepositPaymentInRange && isDepositPaid;

  const { total: totalSalesTax } = getAppointmentSalesTax({
    appointment,
    startDate,
    endDate,
    dateType,
  });

  const customerName = getAppointmentCustomerName(appointment);
  const employeeName = getAppointmentArtistName(appointment);

  const depositPaymentDate =
    appointment.depositPaidOn || appointment.confirmedOn;
  // show N/A when deposit WAS paid but is NOT in range
  const depositValue = hasOutOfRangeDeposit ? 'N/A' : depositAmount;

  const paymentMethod = getAppointmentFullPaymentMethod(appointment.payments);

  return {
    appointmentId: appointment.appointmentId,
    totalSales,
    totalCredit,
    totalFees,
    totalCash,
    totalGiftCard,
    amountSentToArtist,
    amountSentToShop,
    checkoutCompletedOn:
      appointment.checkoutCompletedOn && servicePricePaid > 0
        ? format(new Date(appointment.checkoutCompletedOn), dateFormat)
        : 'N/A',
    depositPaymentDate:
      depositPaymentDate && depositAmount > 0
        ? format(new Date(depositPaymentDate), dateFormat)
        : 'N/A',
    depositPaymentMethod:
      depositPaymentDate && depositAmount > 0
        ? capitalize(appointment.depositPaymentMethod?.toLowerCase() ?? 'N/A')
        : 'N/A',
    artist: employeeName,
    customer: customerName,
    date: appointment.startDateTime
      ? format(new Date(appointment.startDateTime), dateFormat)
      : 'N/A',
    deposit: depositValue,
    paymentMethod: servicePricePaid > 0 ? paymentMethod || 'N/A' : 'N/A',
    servicePricePaid,
    tipAmount,
    salesTax: totalSalesTax,
    refundedAmount,
    voidedAmount,
  };
};

export const getAppointmentTableData = (args: GetAppointmentDataParams) => {
  if (args.dateType === 'payment') {
    return getAppointmentDataByPayment(args);
  }
  return getAppointmentDataByAppointment(args);
};

export const getOrderItemsList = (order: ReportOrder) =>
  order.orderItems
    .map(
      item =>
        `${getOrderItemName(item)}${
          item.quantity > 1 ? ` (${item.quantity})` : ''
        }: ${formatDollarAmount(item.customItemPrice || 0, true)}`,
    )
    .join(', ');

export const getOrderCashSalesTax = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) =>
  getOrderCashSales({
    order,
    includeTaxes: true,
    includeTips: false,
    selectedProducts,
    includeNonSuccess,
  }) -
  getOrderCashSales({
    order,
    includeTaxes: false,
    includeTips: false,
    selectedProducts,
    includeNonSuccess,
  });

export const getOrderCreditSalesTax = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) =>
  getOrderCreditSales({
    order,
    includeTips: false,
    includeTaxes: true,
    selectedProducts,
    includeNonSuccess,
  }) -
  getOrderCreditSales({
    order,
    includeTips: false,
    includeTaxes: false,
    selectedProducts,
    includeNonSuccess,
  });

const applySelectedSalesTax = (
  amounts: {
    cash: number;
    credit: number;
    giftCard: number;
    total: number;
  },
  selectedSalesTax: number,
) => {
  const { cash, credit, giftCard, total } = amounts;
  if (total === 0) {
    return {
      cash: 0,
      credit: 0,
      giftCard: 0,
      total: 0,
    };
  }

  const selectedProportion = selectedSalesTax / total;
  return {
    cash: cash * selectedProportion,
    credit: credit * selectedProportion,
    giftCard: giftCard * selectedProportion,
    total: total * selectedProportion,
  };
};

export const getOrderSalesTax = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) => {
  const payments = filterPayments(order.payments, includeNonSuccess);
  // New orders have sales tax in the metadata
  const hasOrderSalesTax = payments.some(p =>
    checkIfSalesTaxInMetadata(p, true),
  );
  const { selectedTaxAmount } = getSelectedProportion(order, selectedProducts);

  if (hasOrderSalesTax) {
    const { cash, credit, giftCard } = payments.reduce(
      (acc, payment) => {
        const { metadata: md, method } = payment;

        if (hasLineItems(payment)) {
          const amount = getOrderPaymentPaidAmountWithLineItems({
            payment,
            order,
            selectedProducts,
            includeTaxes: true,
            includeTips: false,
            lineItemTypes: ['RETAIL_ITEM_TAX'],
          });
          if (amount > 0) {
            acc.cash += method === 'CASH' ? amount : 0;
            acc.credit +=
              method === 'CREDIT' || method === 'TERMINAL' ? amount : 0;
            acc.giftCard += method === 'GIFT_CARD' ? amount : 0;
          }

          return acc;
        }

        const metadata = md || {};
        if (
          method === 'GIFT_CARD' &&
          'retail_gift_card_sales_tax' in metadata
        ) {
          const salesTax = Number(metadata.retail_gift_card_sales_tax || 0);
          acc.giftCard += salesTax;
        } else if (metadata.order_sales_tax) {
          const salesTax =
            'sales_tax_paid_amount' in metadata
              ? Math.min(
                  metadata.order_sales_tax || 0,
                  metadata.sales_tax_paid_amount || 0,
                  metadata.retail_paid_amount || 0,
                )
              : Number(metadata.order_sales_tax);
          acc.cash += method === 'CASH' ? salesTax : 0;
          acc.credit +=
            method === 'CREDIT' || method === 'TERMINAL' ? salesTax : 0;
          acc.giftCard += method === 'GIFT_CARD' ? salesTax : 0;
        }
        return acc;
      },
      {
        cash: 0,
        credit: 0,
        giftCard: 0,
      },
    );

    return applySelectedSalesTax(
      {
        cash,
        credit,
        giftCard,
        total: cash + credit + giftCard,
      },
      selectedTaxAmount,
    );
  }

  const cash = getOrderCashSalesTax({
    order,
    selectedProducts,
    includeNonSuccess,
  });

  const credit = getOrderCreditSalesTax({
    order,
    selectedProducts,
    includeNonSuccess,
  });

  const giftCard = getGiftCardSalesTax({
    reportObject: order,
    type: 'retail',
    selectedProducts,
    includeNonSuccess,
  });

  const total = cash + credit + giftCard;

  return applySelectedSalesTax(
    {
      cash,
      credit,
      giftCard,
      total,
    },
    selectedTaxAmount,
  );
};

export const getOrderTableData = (args: GetOrderDataParams) => {
  const {
    order,
    includeTips: passedIncludeTips,
    includeTaxes,
    selectedProducts,
  } = args;

  const includeTips =
    args.order.payments.every(p => !p.appointmentId) && passedIncludeTips;

  const totalSales = getOrderTotalSales({
    order,
    includeTips,
    includeTaxes,
    selectedProducts,
  });
  const allProducts = order.orderItems.map(getOrderItemName);
  const creditSales = getOrderCreditSales({
    order,
    includeTaxes: true,
    includeTips: true,
    selectedProducts: allProducts,
  });
  const cashSales = getOrderCashSales({
    order,
    includeTaxes: true,
    includeTips: true,
    selectedProducts: allProducts,
  });
  const giftCardSales = getOrderGiftCardSales({
    order,
    includeTaxes: true,
    includeTips: true,
    selectedProducts: allProducts,
  });

  const { total: salesTax } = getOrderSalesTax({
    order,
    selectedProducts,
  });

  const refundedAmount = Math.min(
    getObjectRefundedAmount({ reportObject: order }),
    creditSales,
  );
  const { cash: voidedCash, giftCard: voidedGiftCard } = getObjectVoidedAmounts(
    {
      reportObject: order,
    },
  );
  const voidedAmount =
    Math.min(cashSales, voidedCash) + Math.min(giftCardSales, voidedGiftCard);

  const customerName = getFullName({
    firstName: order.customer.user.firstName,
    lastName: order.customer.user.lastName,
  });

  const paymentDate = order.completedAt;

  const paymentMethod = dedupeList(
    sortPaymentsByPaymentMethod(order.payments.filter(isPaymentComplete)).map(
      p => getPaymentMethodText(p.method),
    ),
  ).join('/');

  const items = getOrderItemsList(order);

  return {
    orderId: order.orderId,
    paymentId: order.payments[0].id,
    customer: customerName,
    amount: totalSales,
    salesTax,
    items,
    date: paymentDate ? format(new Date(paymentDate), dateFormat) : 'N/A',
    paymentMethod,
    refundedAmount,
    voidedAmount,
  };
};

export const getAppointmentCashTips = ({
  appointment,
  startDate,
  endDate,
  dateType,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  startDate: Date | null;
  endDate: Date | null;
  dateType: DateType;
  includeNonSuccess?: boolean;
}) =>
  getAppointmentCashSales({
    appointment,
    startDate,
    endDate,
    dateType,
    includeTaxes: false,
    includeTips: true,
    includeNonSuccess,
  }) -
  getAppointmentCashSales({
    appointment,
    startDate,
    endDate,
    dateType,
    includeTaxes: false,
    includeTips: false,
    includeNonSuccess,
  });

export const getAppointmentCreditTips = ({
  appointment,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  includeNonSuccess?: boolean;
}) =>
  getAppointmentCreditSales({
    appointment,
    includeTaxes: false,
    includeTips: true,
    includeNonSuccess,
  }) -
  getAppointmentCreditSales({
    appointment,
    includeTaxes: false,
    includeTips: false,
    includeNonSuccess,
  });

export const getAppointmentGiftCardTips = ({
  appointment,
  includeNonSuccess = true,
}: {
  appointment: ReportAppointment;
  includeNonSuccess?: boolean;
}) =>
  getAppointmentGiftCardSales({
    appointment,
    includeTaxes: false,
    includeTips: true,
    includeNonSuccess,
  }) -
  getAppointmentGiftCardSales({
    appointment,
    includeTaxes: false,
    includeTips: false,
    includeNonSuccess,
  });

export const getOrderCreditTips = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) =>
  getOrderCreditSales({
    order,
    includeTaxes: false,
    includeTips: true,
    selectedProducts,
    includeNonSuccess,
  }) -
  getOrderCreditSales({
    order,
    includeTaxes: false,
    includeTips: false,
    selectedProducts,
    includeNonSuccess,
  });

export const getOrderCashTips = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) =>
  getOrderCashSales({
    order,
    includeTaxes: false,
    includeTips: true,
    selectedProducts,
    includeNonSuccess,
  }) -
  getOrderCashSales({
    order,
    includeTaxes: false,
    includeTips: false,
    selectedProducts,
    includeNonSuccess,
  });

export const getOrderGiftCardTips = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) =>
  getOrderGiftCardSales({
    order,
    includeTaxes: false,
    includeTips: true,
    selectedProducts,
    includeNonSuccess,
  }) -
  getOrderGiftCardSales({
    order,
    includeTaxes: false,
    includeTips: false,
    selectedProducts,
    includeNonSuccess,
  });

export const getAppointmentTips = ({
  appointment,
  startDate,
  endDate,
  dateType,
  includeNonSuccess = true,
}: GetAppointmentDataParams) => {
  const cash = getAppointmentCashTips({
    appointment,
    startDate,
    endDate,
    dateType,
    includeNonSuccess,
  });

  const credit = getAppointmentCreditTips({ appointment, includeNonSuccess });

  const giftCard = getAppointmentGiftCardTips({
    appointment,
    includeNonSuccess,
  });

  const total = cash + credit + giftCard;

  return {
    cash,
    credit,
    giftCard,
    total,
  };
};

export const getOrderTips = ({
  order,
  selectedProducts,
  includeNonSuccess = true,
}: {
  order: ReportOrder;
  selectedProducts: string[];
  includeNonSuccess?: boolean;
}) => {
  const cash = getOrderCashTips({
    order,
    selectedProducts,
    includeNonSuccess,
  });

  const credit = getOrderCreditTips({
    order,
    selectedProducts,
    includeNonSuccess,
  });

  const giftCard = getOrderGiftCardTips({
    order,
    selectedProducts,
    includeNonSuccess,
  });

  const total = cash + credit + giftCard;

  return {
    cash,
    credit,
    giftCard,
    total,
  };
};
